import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Group, Layout, Page, Grid, Button } from "@/comn/components";
import { useFetch, useForm, useModal, useToast, useResource, useTree, usePage, useGrid, useAuth } from "@/comn/hooks";
import { OSCI0103018Q } from "@/tz/go/egaz/osci/ptl/usr/orgusr/OSCI0103018Q";
// [2026-10-28] 관리자 권한 보유 여부 판정용
import { ptlUtils } from "@/tz/go/egaz/osci/ptl/comn";
import { APIS, URLS, SF_ORG_USR_INFO, SG_USR_AUTH_LIST, FIXED_USR_AUTH_IDS } from "./services/OsciOrgUsrService";

/*
 * Modifying and Deleting Organization User Management
 * !== 기관 관리 수정 및 삭제 ==!
 */
export const OSCI0103016S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0002"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { usrIdntfId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const auth = useAuth();

    useResource({
        defaultSchema: [
            { area: "orgDeptCd" },
            { area: "cntyCd" },
            { area: "comnCd", comnCd: "OSC0156" },
            { area: "orgCd" },
            { area: "cstmCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization User Information Form !== 기관 사용자 정보 form ==!
        orgUsrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: {},
        }),
    };

    const grid = {
        ogaUsrAuthList: useGrid({ defaultSchema: SG_USR_AUTH_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization User Information !== 기관 사용자 정보 조회 ==!
        getOrgUsrInfo: useFetch({
            api: (data) => APIS.getOrgUsrInfo(usrIdntfId),
            enabled: !!usrIdntfId,
            onSuccess: (data) => {
                const orgUsrInfo = data.orgUsrInfo.content;
                form.orgUsrInfo.setValues(orgUsrInfo);
                fetch.getOgaUsrAuthList.fetch();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00035" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),

        getOgaUsrAuthList: useFetch({
            api: (page = grid.ogaUsrAuthList.page, size = grid.ogaUsrAuthList.size) =>
                APIS.getOgaUsrAuthList({ usrIdntfId: usrIdntfId }, page, size),
            onSuccess: (data) => {
                const ogaUsrAuthList = data.authList.content;
                if (ogaUsrAuthList != null) grid.ogaUsrAuthList.setData(comnUtils.getGridData(ogaUsrAuthList));
            },
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        // Update Organization User Information !== 기관 사용자 정보 수정 ==!
        updateOrgInfo: useFetch({
            api: (info: any) => APIS.updateOrgUsrInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00070",
                    onConfirm: () => navigate(`${URLS.osci0103014q}?menuId=${menuId}`),
                }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
            },
            // Show the reason returned by the server (e.g. the organization already has a representative)
            // instead of the generic failure message. !== 서버가 내려준 실패 사유를 그대로 표시한다 ==!
            onError: (error: any) => {
                const data = error.response?.data;
                const errmsg =
                    data?.errorCode == -1 || comnUtils.isEmpty(data?.errorMessage)
                        ? "msg.osc.00039" // Failed to update. !== 수정이 실패하였습니다.==!
                        : data.errorMessage;
                toast.showToast({ type: "error", content: errmsg });
            },
        }),
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        // 시스템이 관리하는 권한은 체크박스를 막아 삭제할 수 없도록 한다
        checkbox: (row: any) => !FIXED_USR_AUTH_IDS.includes(row.authId),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => navigate(`${URLS.osci0103014q}?menuId=${menuId}`),
        // Click Update Button !== 수정 버튼 클릭 ==!
        onUpdateBtnClick: () => {
            modal.openModal({
                content: "msg.osc.00064", // Confirm update? !== 수정하시겠습니까? ==!
                onConfirm: () => {
                    form.orgUsrInfo.handleSubmit(
                        () => {
                            const data = form.orgUsrInfo.getValues();
                            const ogaUsrAuthList = grid.ogaUsrAuthList.getData();
                            const dataResult = { ...data, ogaUsrAuthList: ogaUsrAuthList };

                            fetch.updateOrgInfo.fetch(dataResult);
                        },
                        () => {
                            toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        },
                    )();
                },
            });
        },

        onBtnGrdAddRowClick: () => {
            const data = grid.ogaUsrAuthList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.osc.00219" }); // Data has been changed. Please save it and proceed. !== 데이터가 변경되었습니다. 저장 후 진행해 주세요. ==!
                return;
            }

            /*
             * [2026-10-28] 관리자로 들어온 경우 관리자 권한(auth_osci0003) 만 선택할 수 있도록 팝업 조회를 고정한다.
             * 기관 대표자는 undefined 를 넘겨 기존과 동일하게 관리자 권한을 제외한 목록을 본다.
             */
            const fixedAuthId = ptlUtils.hasAuth("auth_osci0003", auth) ? "auth_osci0003" : undefined;

            const modalCallback = (list: any) => {
                const authList = grid.ogaUsrAuthList.getData();
                list.forEach(
                    (item: any) =>
                        authList.findIndex((subItem: any) => item.authId === subItem.authId) === -1 &&
                        grid.ogaUsrAuthList.addRow(item),
                );
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "sm",
                content: (
                    <OSCI0103018Q
                        psnTin={form.orgUsrInfo.getValue("psnTin")}
                        fixedAuthId={fixedAuthId}
                        ogaUsrAuthList={grid.ogaUsrAuthList.getData()}
                        callback={modalCallback}
                    />
                ),
            });
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.ogaUsrAuthList.getChecked())) {
                modal.openModal({ content: "msg.osc.00084" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }

            grid.ogaUsrAuthList.deleteRow("checkbox");
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgUsrInfo.fetch();
        form.orgUsrInfo.setSchema("psnTin", { edit: false });
        form.orgUsrInfo.setSchema("orgCd", { edit: false });
    }, []);

    const orgCd = form.orgUsrInfo.watch("orgCd");

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.psnTin} />
                            <Group.Control {...form.orgUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgCd} />
                            <Group.Control
                                {...form.orgUsrInfo.schema.deptCd}
                                popupParams={{ orgCd: orgCd }}
                                // Division 직접 선택 목록에는 현재 기관에 속한 부서만 표시한다.
                                filter={(_option: any, division: any) => division?.orgCd === orgCd}
                            />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgRppnYn} />
                            {/* <Group.Control {...form.orgUsrInfo.schema.frgnrYn} /> */}
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.aplcCntyCd} />
                            <Group.Control {...form.orgUsrInfo.schema.usrMoblNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrEmlAddr} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.rsdtAddr} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            {/* <Group.Control {...form.orgUsrInfo.schema.cstmOfceCd} /> */}
                            <Group.Control {...form.orgUsrInfo.schema.usrStatCd} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_LIST"} />
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid {...grid.ogaUsrAuthList.grid} render={render} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onUpdateBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
